'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ProductVariationDocument } from '@packages/gql/generated/shopping/ProductVariationDocument';
import { ProductVariationFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductVariationFragmentFragmentDoc';
import { AddToBasketButton } from '@packages/modules/src/AddToBasketButton';
import { DownloadsCard } from '@packages/modules/src/DownloadsCard/DownloadsCard';
import { EfficiencyFlagList } from '@packages/modules/src/EfficiencyFlagList/EfficiencyFlagList';
import { LazyRender } from '@packages/modules/src/LazyRender/LazyRender';
import { Price } from '@packages/modules/src/Price/Price';
import { ProductPowerSupply } from '@packages/modules/src/ProductPowerSupply';
import { VariationSelect } from '@packages/modules/src/VariationSelect/VariationSelect';
import { WishlistButtonWithApi } from '@packages/modules/src/WishlistButtonWithApi/WishlistButtonWithApi';
import {
  Box,
  CardContent,
  CardHeader,
  ConditionalWrapper,
  DynamicYield,
  Grid,
  InlineNotification,
  Portal,
  Skeleton,
  Stack,
  SwogoContainer,
  Typography,
} from '@packages/shared';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import DOMPurify from 'isomorphic-dompurify';
import { useParams, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { Suspense, startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { getProductAndFallbackId } from '../../helpers/getProductAndFallbackId';
import { ServiceInformationCard } from '../AdditionalInfo/ServiceInformationCard';
import { UspsCard } from '../AdditionalInfo/UspsCard';
import { AddToBasketLayer } from '../AddToBasketLayer';
import { ArticleServices } from '../ArticleServices';
import { BonusPoints } from '../BonusPoints';
import { CollapsibleCard } from '../CollapsibleCard';
import { CompactProduct } from '../CompactProduct';
import { CustomerRatings } from '../CustomerRatings';
import { DeliveryInfoCard } from '../DeliveryInfoCard';
import { EcoLabelCard } from '../EcoLabelCard';
import { Flixmedia } from '../Flixmedia';
import { ImageGallery } from '../ImageGallery';
import { ImageGalleryColorDialog } from '../ImageGallery/ImageGalleryColorDialog';
import { InstallmentCalculatorCallToAction } from '../InstallmentCalculator/InstallmentCalculatorCallToAction';
import { InstallmentCard } from '../InstallmentCalculator/InstallmentCard';
import { LegalHintsCard } from '../LegalHintsCard';
import { LoadbeeCard } from '../LoadbeeCard';
import { ManufacturerHints } from '../ManufacturerHints';
import { OocvTable } from '../OocvTable';
import { PaymentTypes } from '../PaymentTypes';
import { ProductHeadline } from '../ProductHeadline';
import { ProductHintsCard } from '../ProductHintsCard';
import { PrudsysProductsRecommendations } from '../PrudsysProductsRecommendations';
import { RatingIconsVerified } from '../RatingIconsVerified';
import { Recommendations } from '../Recommendations';
import { SampleOrderLink } from '../SampleOrderLink';
import { SearchProductToProductsRecommendations } from '../SearchProductToProductsRecommendations';
import { SearchReplaceRecommendations } from '../SearchReplaceRecommendations';
import { SellingPoints } from '../SellingPoints';
import { ShareButtonGridItem } from '../ShareButtonGridItem';
import { SimilarProductsRecommendations } from '../SimilarProductsRecommendations';
import { SlotBoxDV } from '../SlotBoxDV';
import { StickyArea } from '../StickyArea';
import { TaxHint } from '../TaxHint';
import { getGlycerinWishlistProductPayload } from '../Tracking/helper/getGlycerinWishlistProductPayload';
import { VoucherPrice } from '../VoucherPrice';
import { bypassBlockMessages, productVariationMessages } from './messages';
import { QuantitySelection } from '../QuantitySelection';

/* GraphQL */ `
  query ProductVariation(
    $skipCliplister: Boolean! = false
    $skipAplus: Boolean! = false
    $locale: String!
    $lang: String!
    $productId: String!
    $fallbackId: String!
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      ...ProductVariationFragment
    }
  }
`;

/* GraphQL */ `
  fragment ProductVariationFragment on Product {
    akl
    # needed for aPlus
    hasServices
    # needed for aPlus
    itemOptionCommKey
    longDescription
    name
    orderNumber
    promotion
    # needed to check if SampleOrder Data is available for the product
    sampleOrderType
    sellingPoints
    sku
    sizeHint
    additionalInfo {
      usps {
        ...UspsCardFragment
      }
      delivery {
        ...ServiceInformationCardDeliveryFragment
      }
      additionalCosts {
        ...ServiceInformationCardAdditionalCostsFragment
      }
    }
    aplus(language: $lang) @skip(if: $skipAplus) {
      ...ArticleServicesFragment
    }
    availability {
      state
      message
    }
    bonuspoints {
      ...BonusPointsFragment
    }
    brand {
      name
    }
    delivery {
      deliverySize
      ...TaxHintFragment
    }
    dimensions {
      values {
        displayText
      }
      ...DimensionFragment
    }
    installment(source: "pdv", paymentpause: false) {
      ...InstallmentCalculatorCallToActionFragment
      numberOfInstallments
    }
    oocv {
      ...OocvTableFragment
    }
    paymentTypes {
      keys
    }
    price {
      value
      valueOld
      currency
      ...ProductPriceFragment
      ...VoucherPriceFragment
      ...ArticleServicesCurrencyFragment
    }
    powerEfficiencyFlags {
      color
      level
      link
      text
      hasOldLabel
    }
    seoRatingIcons: seoRating(locale: $locale) {
      product {
        ...RatingIconsVerifiedFragment
      }
      ...RatingSeoResultFragment
    }
    tireEfficiency {
      link
      flags {
        level
        type
      }
    }
    ...AddToBasketFragment
    ...CompactProductFragment
    ...CustomerRatingsFragment
    ...DeliveryInfoCardFragment
    ...DownloadsCardFragment
    ...EcoLabelCardFragment
    ...EfficiencyFlagListFragment
    ...FlixmediaFragment
    ...GetGlycerinWishlistProductPayloadFragment
    ...HeadlineFragment
    ...ImageGalleryFragment
    ...ImageGalleryColorDialogFragment
    ...LegalHintsCardFragment
    ...LoadbeeFragment
    ...ManufacturerHints
    ...PaymentTypesFragment
    ...ProductHintsCardFragment
    ...PrudsysProductsRecommendations
    ...RecommendationsFragment
    ...SampleOrderLinkFragment
    ...SearchProductToProductsRecommendations
    ...SearchReplaceRecommendations
    ...SimilarProductsRecommendations
    powerSupply {
      ...ProductPowerSupplyFragment
    }
  }
`;

export const ProductVariation = ({
  productData,
  kimRatingSlot,
  seoListSlot,
}: {
  productData: FragmentType<typeof ProductVariationFragmentFragmentDoc>;
  kimRatingSlot: ReactNode;
  seoListSlot: ReactNode;
}) => {
  const imageGalleryRef = useRef<HTMLDivElement>(null);
  const [selectedVariation, setSelectedVariation] = useState<string | null>(null);
  const { isDesktop, isMobile } = useDeviceType();
  const stickyRef = useRef<HTMLElement | undefined>(null);

  const { language, locale } = useI18n();
  const { formatMessage } = useIntl();

  const params = useParams<{ akl: string }>();
  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') || null;

  const {
    dynamicYieldEnabled,
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
    product: {
      aplus: { enabled: aplusEnabled },
      cliplister: { enabled: cliplisterEnabled },
      flixmedia: { enabled: flixmediaEnabled },
      hasSwogo,
      recommendations: {
        dv: {
          slots: { '1': recoSlot1, '4': recoSlot4 },
        },
      },
      voucherPrice: { enabled: voucherPriceEnabled },
    },
  } = useConfig();

  const [{ data }] = useQuery({
    query: ProductVariationDocument,
    pause: !selectedVariation,
    variables: {
      ...getProductAndFallbackId(params?.akl, selectedVariation!),
      locale,
      lang: language,
      skipCliplister: !cliplisterEnabled,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const productVariation = getFragmentData(
    ProductVariationFragmentFragmentDoc,
    data?.product ?? productData,
  );

  useEffect(() => {
    stickyRef.current = document.getElementById('sticky-portal') || undefined;
  }, []);

  return (
    <>
      <Grid container item rowSpacing={0.5} columnSpacing={5.3}>
        <Grid item xs={12} md={6} lg={7}>
          <BypassBlock
            buttonText={formatMessage(bypassBlockMessages.bypassImageGallery)}
            skipScrollIntoView={!isMobile}
            className="h-full"
          >
            <ImageGallery
              data={productVariation}
              ref={imageGalleryRef}
              colorPicker={
                <ImageGalleryColorDialog
                  data={productVariation}
                  onChange={(sku) => startTransition(() => setSelectedVariation(sku))}
                />
              }
            />
          </BypassBlock>
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          <Grid
            container
            spacing={1.5}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            sx={{
              px: { xs: 1.5, md: 1.5, lg: 0 },
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h1">
                {productVariation.name}
              </Typography>
            </Grid>
            {productVariation.seoRatingIcons && productVariation.seoRatingIcons.product && (
              <Grid item xs={12}>
                <RatingIconsVerified data={productVariation.seoRatingIcons.product} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Price data={productVariation.price} />
              <TaxHint data={productVariation.delivery} />
              {
                /* Geizhals Einsprung -> VoucherPrice */
                voucherPriceEnabled && <VoucherPrice data={productVariation.price} />
              }
              {dynamicYieldEnabled && (
                <DynamicYield dataDyId="dy_info_socialproof_dv" sx={{ mt: 1 }} />
              )}
            </Grid>
            {productVariation.bonuspoints && (
              <Grid item xs={12}>
                <BonusPoints data={productVariation.bonuspoints} />
              </Grid>
            )}
            {productVariation.paymentTypes && (
              <Grid item xs={12}>
                <PaymentTypes data={productVariation} />
              </Grid>
            )}
            {productVariation.installment &&
              productVariation.installment.numberOfInstallments > 0 &&
              InstallmentCalculatorCallToActionLayout && (
                <Grid item xs={12}>
                  <InstallmentCalculatorCallToAction
                    installmentData={productVariation.installment}
                    basePrice={productVariation.price.value}
                  />
                </Grid>
              )}
            {/* Power Supply // Fuag */}
            {productVariation.powerSupply && (
              <Grid item xs={12}>
                <ProductPowerSupply
                  maskedData={productVariation.powerSupply}
                  asCard
                  showPowerSupplyMessage
                />
              </Grid>
            )}

            {/* TODO: maybe move the grid inside of EfficiencyFlagList, so we dont have to put tireEfficiency
             * and powerEfficiencyFlags AND EfficiencyFlagListFragment in the fragment
             */}
            {(productVariation.tireEfficiency || productVariation.powerEfficiencyFlags) && (
              <Grid item xs={12}>
                <EfficiencyFlagList data={productVariation} />
              </Grid>
            )}
            {dynamicYieldEnabled && <DynamicYield dataDyId="dv_flag" />}
            {hasSwogo && <SwogoContainer position={0} />}
            {/* TODO: ask how quantitySelection should be handled  */}
            <Grid item xs={12}>
              <QuantitySelection />
            </Grid>
            <Grid item xs={12}>
              <Suspense>
                {/* TODO: ask UX how margin bottom or spacings in general should be in variation select (its all over the place in zeplin at the moment) */}
                <Stack spacing={3} mb={2.5}>
                  {productVariation.dimensions.map(
                    (dimension, i) =>
                      dimension.values.length > 0 && (
                        <VariationSelect
                          dimensionData={dimension}
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          onChange={(variation) =>
                            startTransition(() => setSelectedVariation(variation))
                          }
                          selectedVariation={selectedVariation}
                          sampleOrderLink={
                            productVariation.sampleOrderType !== null ? (
                              <SampleOrderLink sx={{ mt: 1.5 }} data={productVariation} />
                            ) : null
                          }
                        />
                      ),
                  )}
                </Stack>
              </Suspense>
            </Grid>
            {productVariation.sizeHint && (
              <Grid item xs={12}>
                <InlineNotification severity="info">{productVariation.sizeHint}</InlineNotification>
              </Grid>
            )}
            <Grid item xs={12}>
              <DeliveryInfoCard data={productVariation} />
            </Grid>
            {aplusEnabled && productVariation.aplus && (
              <Grid item xs={12}>
                <ArticleServices
                  data={productVariation.aplus}
                  currencyData={productVariation.price}
                />
              </Grid>
            )}
            {isDesktop && (
              <Grid item xs={12}>
                <Suspense>
                  <Stack
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexDirection: 'row',
                    }}
                  >
                    <WishlistButtonWithApi
                      trackingProps={{
                        products: [
                          getGlycerinWishlistProductPayload({
                            data: productVariation,
                            refSearchParam: ref,
                          }),
                        ],
                      }}
                      sku={productVariation.sku}
                      productName={productVariation.name}
                      variant="contained"
                      sx={{ flexShrink: 0 }}
                      availability={productVariation.availability.state}
                    />
                    <AddToBasketButton data={productVariation} sx={{ fontSize: '1rem' }}>
                      {(layerData, lineItem) => (
                        <AddToBasketLayer data={layerData} lineItem={lineItem} />
                      )}
                    </AddToBasketButton>
                  </Stack>
                </Suspense>
                {dynamicYieldEnabled && (
                  <DynamicYield dataDyId="dy_button_rabattaktion_dv" sx={{ mt: 1 }} />
                )}
              </Grid>
            )}
            <ShareButtonGridItem title={productVariation.name} text={productVariation.name} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <BypassBlock buttonText={formatMessage(bypassBlockMessages.bypassRecommendations)}>
          {
            {
              prudsysProducts: <PrudsysProductsRecommendations data={productVariation} />,
              searchProductToProducts: (
                <SearchProductToProductsRecommendations data={productVariation} />
              ),
            }[recoSlot1]
          }
        </BypassBlock>
      </Grid>
      <Grid item xs={12}>
        <ConditionalWrapper
          condition={isMobile}
          wrapper={(ch) => (
            <LazyRender
              key={`product-details-${productVariation.sku}`}
              fallback={<Skeleton height={550} />}
            >
              {ch}
            </LazyRender>
          )}
        >
          <Box>
            {hasSwogo && <SwogoContainer position={1} />}
            <BypassBlock buttonText={formatMessage(bypassBlockMessages.bypassDetailsAndInfo)}>
              <SlotBoxDV mode="headline">
                <Typography sx={{ my: 2, typography: ['h4', 'h4', 'h3'] }} component="header">
                  {formatMessage(productVariationMessages.slotTitle)}
                </Typography>
              </SlotBoxDV>
              <SlotBoxDV mode="slotbox">
                <Grid container columnSpacing={5.75} rowSpacing={1.5}>
                  <Grid item xs={12} lg={7}>
                    <Stack spacing={1.5}>
                      <CollapsibleCard
                        cardActionText={{
                          collapseText: formatMessage(productVariationMessages.collapseCardText),
                          expandText: formatMessage(productVariationMessages.expandCardText),
                        }}
                        collapseChildren={
                          <>
                            {productVariation.oocv &&
                              productVariation.oocv.map((oocvTable, idx) => (
                                /* eslint-disable-next-line react/no-array-index-key */
                                <Stack sx={{ px: 2, mt: 1.5 }} spacing={1.5} key={idx}>
                                  <OocvTable oocvTableData={oocvTable} />
                                </Stack>
                              ))}
                            <ManufacturerHints
                              data={productVariation}
                              className="mb-2.5 mt-1.5 px-2"
                            />
                          </>
                        }
                        collapsedSize="6rem"
                      >
                        <CardHeader
                          title={formatMessage(productVariationMessages.collapsibleCardHeader)}
                          sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
                        />
                        <CardContent sx={{ pb: 0 }}>
                          <ProductHeadline productData={productVariation} />
                          <Stack gap={1}>
                            <div className="pl-2">
                              <SellingPoints
                                sellingPoints={productVariation.sellingPoints}
                                orderNumber={productVariation.orderNumber}
                                promotion={productVariation.promotion}
                              />
                            </div>
                            {productVariation.longDescription && (
                              <div
                                className="text-size-body2"
                                /* eslint-disable-next-line react/no-danger */
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(productVariation.longDescription),
                                }}
                              />
                            )}
                            {flixmediaEnabled &&
                              productVariation.brand?.name.toLowerCase() === 'dyson' && (
                                <Flixmedia productData={productVariation} />
                              )}
                          </Stack>
                        </CardContent>
                      </CollapsibleCard>
                      <LoadbeeCard data={productVariation} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <Stack spacing={1.5}>
                      {(productVariation.additionalInfo?.additionalCosts ||
                        productVariation.additionalInfo?.delivery) && (
                        <ServiceInformationCard
                          additionalCostsData={productVariation.additionalInfo.additionalCosts}
                          deliveryData={productVariation.additionalInfo.delivery}
                        />
                      )}
                      {productVariation.additionalInfo?.usps && (
                        <UspsCard data={productVariation.additionalInfo.usps} />
                      )}
                      <EcoLabelCard ecoLabelsData={productVariation} />
                      <ProductHintsCard productHintsData={productVariation} />
                      <LegalHintsCard legalHintsData={productVariation} />
                      <DownloadsCard data={productVariation} />

                      {productVariation.installment &&
                        productVariation.installment.numberOfInstallments > 0 &&
                        InstallmentCalculatorCallToActionLayout &&
                        InstallmentCalculatorCallToActionLayout.layout === 'Layout3' &&
                        InstallmentCalculatorCallToActionLayout.openCalculatorIn.type ===
                          'Card' && (
                          <InstallmentCard
                            basePrice={productVariation.price.value}
                            cardID={
                              InstallmentCalculatorCallToActionLayout.openCalculatorIn.calculatorID
                            }
                          />
                        )}
                      {dynamicYieldEnabled && (
                        <>
                          <DynamicYield dataDyId="dv_badge" />
                          <DynamicYield dataDyId="dv-horizontal-banner" />
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </SlotBoxDV>
            </BypassBlock>
          </Box>
        </ConditionalWrapper>
      </Grid>
      <Grid item xs={12}>
        <Suspense>
          <BypassBlock buttonText={formatMessage(bypassBlockMessages.bypassRecommendations)}>
            <Recommendations recosData={productVariation} />
          </BypassBlock>
        </Suspense>
      </Grid>
      <Grid item xs={12}>
        {productVariation.seoRatingIcons && (
          <Suspense>
            <BypassBlock buttonText={formatMessage(bypassBlockMessages.bypassCustomerRatings)}>
              <CustomerRatings
                productData={productVariation}
                ratingResultData={productVariation.seoRatingIcons}
              />
            </BypassBlock>
          </Suspense>
        )}
      </Grid>
      <Grid item xs={12}>
        <BypassBlock buttonText={formatMessage(bypassBlockMessages.bypassRecommendations)}>
          {
            {
              similarProducts: <SimilarProductsRecommendations data={productVariation} />,
              searchReplace: <SearchReplaceRecommendations data={productVariation} />,
            }[recoSlot4]
          }
        </BypassBlock>
      </Grid>
      {kimRatingSlot}
      <BypassBlock buttonText={formatMessage(bypassBlockMessages.bypassSeoList)}>
        <SlotBoxDV mode="seo">{seoListSlot}</SlotBoxDV>
      </BypassBlock>
      <Portal container={stickyRef.current}>
        <StickyArea attachedNodeRef={imageGalleryRef}>
          <CompactProduct data={productVariation} withProductName={isDesktop} />
          <Box display="flex" flexDirection="column">
            <Stack direction="row" gap={1} alignItems="center" sx={{ bgcolor: 'common.white' }}>
              <WishlistButtonWithApi
                trackingProps={{
                  products: [
                    getGlycerinWishlistProductPayload({
                      data: productVariation,
                      refSearchParam: ref,
                    }),
                  ],
                }}
                variant="contained"
                sku={productVariation.sku}
                sx={{ flexShrink: 0 }}
                productName={productVariation.name}
                availability={productVariation.availability.state}
              />
              <AddToBasketButton
                data={productVariation}
                sx={{ fontSize: '1rem', minHeight: '2.75rem' }}
              >
                {(layerData, lineItem) => <AddToBasketLayer data={layerData} lineItem={lineItem} />}
              </AddToBasketButton>
            </Stack>
            {dynamicYieldEnabled && (
              <DynamicYield dataDyId="dy_button_rabattaktion_dv" sx={{ mt: 1 }} />
            )}
          </Box>
        </StickyArea>
      </Portal>
    </>
  );
};
